/**
 * CPF, email, and phone validation shared between client and server.
 *
 * CPF check-digit algorithm: well-known mod-11 ("dígitos verificadores").
 * Treats common all-equal-digit strings (000.000.000-00 etc.) as invalid.
 */

import { digitsOnly, parsePhone, toE164 } from "./phone.js";

export function formatCPF(input: string): string {
  const numbers = digitsOnly(input).slice(0, 11);
  return numbers
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function isValidCPF(input: string): boolean {
  const cpf = (input ?? "").replace(/\D/g, "");
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const calc = (slice: string): number => {
    let sum = 0;
    for (let i = 0; i < slice.length; i++) {
      sum += parseInt(slice[i]!, 10) * (slice.length + 1 - i);
    }
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  const d1 = calc(cpf.slice(0, 9));
  const d2 = calc(cpf.slice(0, 10));
  return d1 === parseInt(cpf[9]!, 10) && d2 === parseInt(cpf[10]!, 10);
}

export function normalizeCPF(input: string): string {
  return (input ?? "").replace(/\D/g, "");
}

/** Full name must contain at least two non-empty words (e.g. "Maria Silva"). */
export function isValidFullName(input: string): boolean {
  const parts = (input ?? "").trim().split(/\s+/).filter(Boolean);
  return parts.length >= 2;
}

export function isValidEmail(input: string): boolean {
  if (!input) return false;
  // Pragmatic email regex; full RFC 5322 is overkill for checkout.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
}

/** Trim + lowercase. Emails are case-insensitive; we always store them lowercase. */
export function normalizeEmail(input: string): string {
  return (input ?? "").trim().toLowerCase();
}

// Common domain misspellings → correct domain (curated, no dependency).
const DOMAIN_TYPOS: Record<string, string> = {
  "gmail.com.br": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.cm": "gmail.com",
  "gmail.comm": "gmail.com",
  "gmai.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmil.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gnail.com": "gmail.com",
  "hotmail.co": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotmail.com.br.com": "hotmail.com.br",
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotmail.cm": "hotmail.com",
  "outlook.co": "outlook.com",
  "outlook.con": "outlook.com",
  "outlok.com": "outlook.com",
  "yahoo.co": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "yaho.com": "yahoo.com",
  "icloud.co": "icloud.com",
  "icloud.con": "icloud.com",
};

// TLDs we treat as valid enough for checkout. Anything else with a clearly
// broken/short TLD is flagged. Kept small and pragmatic.
const KNOWN_TLDS = new Set([
  "com",
  "com.br",
  "net",
  "net.br",
  "org",
  "org.br",
  "br",
  "edu",
  "edu.br",
  "gov",
  "gov.br",
  "io",
  "co",
  "app",
  "ai",
  "me",
  "info",
  "biz",
  "pt",
  "es",
  "us",
  "uk",
  "dev",
]);

/**
 * Suggest a corrected email for common mistakes (domain typos + broken TLDs).
 * Returns the corrected email (already normalized) or null when nothing to fix.
 * Purely heuristic — never blocks a genuinely valid address on its own.
 */
export function suggestEmailFix(input: string): string | null {
  const email = normalizeEmail(input);
  const at = email.lastIndexOf("@");
  if (at <= 0 || at === email.length - 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (!domain.includes(".")) return null;

  // 1) Direct domain typo table (curated — includes gmail.com.br -> gmail.com).
  if (DOMAIN_TYPOS[domain]) {
    const fixed = `${local}@${DOMAIN_TYPOS[domain]}`;
    return fixed !== email ? fixed : null;
  }

  // 2) Broken final label that looks like a typo of "com" (e.g. "gmail.con",
  //    "domain.comm", "site.c"). We only rewrite the last label to keep this
  //    conservative and avoid touching legitimate ccTLDs like ".com.br".
  const lastLabel = domain.slice(domain.lastIndexOf(".") + 1);
  const looksLikeComTypo = ["con", "cim", "cm", "comm", "vom", "xom", "c"].includes(lastLabel);
  if (looksLikeComTypo) {
    const head = domain.slice(0, domain.lastIndexOf("."));
    return `${local}@${head}.com`;
  }

  return null;
}

/**
 * Stronger email check than `isValidEmail`: also requires a plausible TLD and
 * rejects addresses that have an obvious correction (typo). Use on submit.
 */
export function isLikelyValidEmail(input: string): boolean {
  const email = normalizeEmail(input);
  if (!isValidEmail(email)) return false;
  const domain = email.slice(email.lastIndexOf("@") + 1);
  if (!domain.includes(".")) return false;
  const tld = domain.slice(domain.indexOf(".") + 1);
  // Accept if the multi-label or single-label TLD is known, else require the
  // final label to be at least 2 chars and alphabetic.
  const finalLabel = domain.slice(domain.lastIndexOf(".") + 1);
  if (KNOWN_TLDS.has(tld) || KNOWN_TLDS.has(finalLabel)) return true;
  return /^[a-z]{2,}$/.test(finalLabel);
}

export function normalizePhoneBR(input: string): string {
  // Keep only digits, ensure leading "+55" is stripped to digits for OpenPix/Stripe friendly storage.
  return (input ?? "").replace(/\D/g, "");
}

export function isValidPhoneBR(input: string): boolean {
  const digits = normalizePhoneBR(input);
  // Brazilian mobile: 11 digits (DDD + 9XXXXXXXX). Accept 10-13 to be lenient with country code.
  return digits.length >= 10 && digits.length <= 13;
}

/**
 * Generic E.164-compatible validator used by the international-aware
 * checkout. The frontend ships full country-by-country rules in
 * `shared/phone.ts`; here on the backend we only sanity-check the total
 * digit count (8–16), since each provider re-validates against its own
 * rules anyway (OpenPix needs a BR CPF + national format, Stripe is global).
 */
export function isValidPhoneIntl(input: string): boolean {
  const digits = (input ?? "").replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 16;
}

/** Normalises any input into an E.164 string with leading "+". */
export function normalizePhoneE164(input: string, defaultIso = "BR"): string {
  const raw = (input ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) {
    const digits = raw.replace(/\D/g, "");
    return digits ? `+${digits}` : "";
  }
  const parsed = parsePhone(raw, defaultIso);
  return toE164(parsed.country, parsed.local);
}
